'use client';

import { useState, MouseEvent, KeyboardEvent } from 'react';
import { UtilityNav, LoadingIndicator } from '@petsmart-ui/sparky';
import { NAVIGATION_URLS } from '@libs/core';
import { useUserSession, DEVICE_TYPE, useDeviceType, usePageLayout, AccountDrawerType } from '@libs/common-next';
import { dataLayerPush, getBaseUrlJoined, layoutEvents } from '@libs/analytics';
import { useRouter } from 'next/navigation';
import { SignIn } from './SignIn';
import { LoggedIn } from './LoggedIn';
import { LoggedInDrawer } from './LoggedInDrawer';
import { useAccountDropdown } from './hooks/useAccountDropdown';
import './UtilityIcons.scss';

export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerClick });

type UtilityIconsProps = {
    showAccountIcon?: boolean;
};

export function UtilityIcons({ showAccountIcon = true }: Readonly<UtilityIconsProps>) {
    const [isActive, setisActive] = useState(false);
    const { isLoading, firstName, loyaltyPoints, currentMemberLevel, availableDollars, qty } = useAccountDropdown();
    const router = useRouter();
    const { openAccountDrawer } = usePageLayout();
    const { user } = useUserSession();
    const isDesktop = useDeviceType() === DEVICE_TYPE.DESKTOP;

    const handleClickPersonIcon = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (isLoading) {
            return null;
        }
        const clickedElement = document.querySelector('[data-testid="account-icon"] svg');
        const target = e.target as Node;
        if (!clickedElement || (target !== clickedElement && !clickedElement.contains(target))) return;

        const path = user ? NAVIGATION_URLS.velocity.account.treatOffers : NAVIGATION_URLS.velocity.signIn;

        if (isDesktop) {
            router.push(path);
        } else if (user) {
            setisActive(true);
        } else {
            openAccountDrawer(AccountDrawerType.SIGN_IN);
        }
    };

    function closeFilterDrawer(e: MouseEvent | KeyboardEvent) {
        if (e) {
            e.stopPropagation();
        }
        setisActive(false);
    }

    return (
        <UtilityNav className="utility__primary-nav">
            {isLoading && (
                <li className="sparky-c-utility-nav__item">
                    <LoadingIndicator iconSize="lg" className="sparky-c-utility-nav__link loading-color" />
                </li>
            )}
            {!isLoading && showAccountIcon && (
                <UtilityNav.Item
                    data-testid="account-icon"
                    hideText
                    text={isActive ? 'Close' : 'Account'}
                    iconName={isActive ? 'x' : 'account-circle-outline'}
                    className="account-icon"
                    onClick={handleClickPersonIcon}
                >
                    {user && !isDesktop && (
                        <LoggedInDrawer
                            firstName={firstName}
                            loyaltyPoints={loyaltyPoints}
                            currentTierLevel={currentMemberLevel}
                            availableDollars={availableDollars}
                            isActive={isActive}
                            onClose={closeFilterDrawer}
                        />
                    )}
                    {isDesktop &&
                        (!isLoading && firstName ? (
                            <div className="signin-flyout">
                                <LoggedIn
                                    firstName={firstName}
                                    loyaltyPoints={loyaltyPoints}
                                    currentTierLevel={currentMemberLevel}
                                    availableDollars={availableDollars}
                                />
                            </div>
                        ) : (
                            <div className="signin-flyout">
                                <SignIn />
                            </div>
                        ))}
                </UtilityNav.Item>
            )}
            <UtilityNav.Item
                data-testid="cart-icon"
                hideText
                href={NAVIGATION_URLS.velocity.cart}
                className="shopping-cart"
                text="Checkout"
                iconName="cart"
                notificationText={qty?.toString()}
                onClick={() =>
                    dataLayerPush(
                        trackingData({
                            selection: 'cart',
                            link_url: getBaseUrlJoined(NAVIGATION_URLS.velocity.cart),
                        })
                    )
                }
            />
        </UtilityNav>
    );
}
